import { useEffect, useState, useCallback } from 'react';
import pkg from '../../package.json';
import { config } from '../core/config';

const { version } = pkg;

/**
 * Хук для управления версией приложения
 * Проверяет версию при загрузке и каждые 5 минут с хостинга
 * При обнаружении новой версии принудительно обновляет приложение
 */
const useAppVersion = () => {
  const [isUpdating, setIsUpdating] = useState(false);

  // Функция для принудительного обновления приложения
  const forceUpdateApp = useCallback(async (oldVer, newVer) => {
    try {
      setIsUpdating(true);
      
      console.log(`Force updating app: ${oldVer} → ${newVer}`);
      
      // Устанавливаем новую версию в localStorage
      localStorage.setItem('appVersion', newVer);

      // Очистка кеша браузера
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('Browser cache cleared');
      }

      // Очистка localStorage (кроме важных данных)
      // const importantKeys = ['appVersion', 'token']; // Добавьте ключи, которые нужно сохранить
      // const keysToRemove = [];
      // for (let i = 0; i < localStorage.length; i++) {
      //   const key = localStorage.key(i);
      //   if (key && !importantKeys.includes(key)) {
      //     keysToRemove.push(key);
      //   }
      // }
      // keysToRemove.forEach(key => localStorage.removeItem(key));

      // Перезагрузка страницы с принудительной загрузкой из сервера
      window.location.reload(true);
    } catch (error) {
      console.error('Error updating app:', error);
      // Если что-то пошло не так, всё равно перезагружаем страницу
      window.location.reload(true);
    }
  }, []);

  // Функция для проверки версии с хостинга
  const checkVersionFromServer = useCallback(async () => {
    try {
      const response = await fetch(config.versionUrl, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        console.warn(`Failed to fetch version from server: ${response.status} ${response.statusText}`);
        return;
      }

      const serverVersionData = await response.json();
      
      if (!serverVersionData || !serverVersionData.version) {
        console.warn('Invalid version data from server:', serverVersionData);
        return;
      }

      const serverVersion = serverVersionData.version;
      const currentAppVersion = version;

      console.log(`Version check: server=${serverVersion}, client=${currentAppVersion}`);

      // Проверяем, отличается ли версия на сервере от текущей - сразу обновляем
      if (serverVersion !== currentAppVersion) {
        console.log(`Version update detected: ${currentAppVersion} → ${serverVersion}`);
        await forceUpdateApp(currentAppVersion, serverVersion);
      }
    } catch (error) {
      console.error('Error checking version from server:', error);
    }
  }, [forceUpdateApp]);

  useEffect(() => {
    const currentAppVersion = version;
    const storedAppVersion = localStorage.getItem('appVersion');

    // Проверяем, изменилась ли версия приложения при первом запуске - сразу обновляем
    if (storedAppVersion && storedAppVersion !== currentAppVersion) {
      console.log(`App version updated: ${storedAppVersion} → ${currentAppVersion}`);
      forceUpdateApp(storedAppVersion, currentAppVersion);
      return; // Выходим, так как будет перезагрузка
    } else {
      // Если версия не изменилась или это первый запуск, просто устанавливаем её в localStorage
      localStorage.setItem('appVersion', currentAppVersion);
    }

    // Запускаем первую проверку версии с сервера
    checkVersionFromServer();

    // Устанавливаем интервал для проверки версии каждые 5 минут
    const intervalId = setInterval(checkVersionFromServer, 1 * 60 * 1000);

    // Очистка при размонтировании
    return () => {
      clearInterval(intervalId);
    };
  }, [checkVersionFromServer, forceUpdateApp]);

  // Компонент индикатора обновления (может не успеть отобразиться)
  const updateIndicator = isUpdating ? (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      left: '20px',
      background: '#EA394B',
      color: 'white',
      padding: '10px 15px',
      borderRadius: '5px',
      fontSize: '14px',
      zIndex: 9999,
      boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
    }}>
      Обновление версии...
    </div>
  ) : null;

  return { 
    version, 
    updateIndicator,
    checkVersionFromServer // Для принудительной проверки версии
  };
};

export default useAppVersion;
